import { type RefObject, useRef } from 'react';
import { releasesSwipe, useHorizontalDrag } from './use-horizontal-drag';

/** How far a row moves at most past its action, under a finger dragging on. */
const RESISTANCE = 0.4;

/** Where a row dragged `dx` sits: with the finger up to its width, then resisting. */
export function rowOffset(dx: number, width: number): number {
  return dx <= width ? dx : width + (dx - width) * RESISTANCE;
}

/**
 * A row swiped right runs its action, like a mail app's: it moves under the finger over the
 * action behind it (`--swipe-progress`, 0 to 1, tells how close it is), and released far
 * enough (or flicked) runs `onCommit` as it slides back. `onCommitLeft` is the action of a
 * swipe left (the drawer's rows archive), shown with `--swipe-left-progress`.
 */
export function useSwipeAction(
  ref: RefObject<HTMLElement | null>,
  onCommit: (() => void) | null,
  enabled: boolean,
  onCommitLeft: (() => void) | null = null,
): void {
  const commit = useRef(onCommit);
  commit.current = onCommit;
  const commitLeft = useRef(onCommitLeft);
  commitLeft.current = onCommitLeft;
  const armed = useRef(false);
  const place = (element: HTMLElement, dx: number) => {
    const width = element.clientWidth;
    const offset = Math.sign(dx) * rowOffset(Math.abs(dx), width / 3);
    element.style.transform = dx === 0 ? '' : `translateX(${offset}px)`;
    element.style.setProperty(
      '--swipe-progress',
      String(Math.max(0, Math.min(1, dx / (width / 3)))),
    );
    element.style.setProperty(
      '--swipe-left-progress',
      String(Math.max(0, Math.min(1, -dx / (width / 3)))),
    );
  };
  const settle = () => {
    const element = ref.current;
    if (element === null) return;
    element.style.transition = '';
    place(element, 0);
    armed.current = false;
  };
  useHorizontalDrag(ref, {
    enabled,
    accepts: () => true,
    allows: (dx) => (dx > 0 ? commit.current !== null : commitLeft.current !== null),
    onDrag: (dx) => {
      const element = ref.current;
      if (element === null) return;
      element.style.transition = 'none';
      place(element, dx);
      // A tick as the action arms (and disarms).
      const ready = Math.abs(dx) > element.clientWidth / 3;
      if (ready !== armed.current) navigator.vibrate?.(8);
      armed.current = ready;
    },
    onRelease: (dx, velocity) => {
      const width = ref.current?.clientWidth ?? 0;
      settle();
      if (!releasesSwipe({ dx, width, velocity })) return;
      if (dx > 0) commit.current?.();
      else commitLeft.current?.();
    },
    onCancel: settle,
  });
}
